// ORTAK "WhatsApp ile gönder" butonu + önizleme penceresi.
//
// Her modül aynı bileşeni kullanır: <WhatsAppGonder modul="puantaj" kayitId={id} />
// Sunucu (routes/whatsapp_bildirim.py) alıcıları, varsayılan konu/açıklamayı
// ve şablon metnini verir; LİNKİ SUNUCU ÜRETİR — buradan yalnız alıcı seçimi
// ve düzenlenmiş açıklama gider. Gönderim whatsapp_messages'a kaynak modül ve
// kayıtla düşer.
import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { MessageCircle, AlertTriangle, ExternalLink, CheckCheck, Check } from 'lucide-react';
import { toast } from 'sonner';

export const DURUM = {
  hazirlaniyor: { etiket: 'Hazırlanıyor', sinif: 'bg-[#8a7e70]/10 text-[#8a7e70]' },
  gonderildi: { etiket: 'Gönderildi', sinif: 'bg-[#4a6a8a]/10 text-[#4a6a8a]' },
  iletildi: { etiket: 'İletildi', sinif: 'bg-[#3d6e4e]/10 text-[#3d6e4e]' },
  okundu: { etiket: 'Okundu', sinif: 'bg-[#3d6e4e]/20 text-[#2d5a3c]' },
  basarisiz: { etiket: 'Başarısız', sinif: 'bg-[#9b3b2f]/10 text-[#9b3b2f]' },
  deneme: { etiket: 'Deneme (gönderilmedi)', sinif: 'bg-[#7a6a2e]/10 text-[#7a6a2e]' },
  engellendi: { etiket: 'Test listesi dışı', sinif: 'bg-[#7a6a2e]/10 text-[#7a6a2e]' },
  gecersiz_numara: { etiket: 'Geçersiz numara', sinif: 'bg-[#9b3b2f]/10 text-[#9b3b2f]' },
  alindi: { etiket: 'Gelen mesaj', sinif: 'bg-[#5b4a8a]/10 text-[#5b4a8a]' },
};

export const DurumRozeti = ({ durum }) => {
  const d = DURUM[durum] || { etiket: durum, sinif: 'bg-[#8a7e70]/10 text-[#8a7e70]' };
  return <span className={`status-badge ${d.sinif}`}>{d.etiket}</span>;
};

const tarihSaat = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

const sureMetni = (saat) => {
  if (!saat) return 'süresiz (kalıcı bağlantı kapatılınca kapanır)';
  return saat % 24 === 0 ? `${saat / 24} gün geçerli` : `${saat} saat geçerli`;
};

// Sunucunun parametre temizliğiyle aynı: satır sonu/sekme/çoklu boşluk → tek boşluk
const temizle = (m) => String(m || '').split(/\s+/).filter(Boolean).join(' ');

function Onizleme({ metin, buton }) {
  return (
    <div className="rounded-xl bg-[#e5ddd5] p-3">
      <div className="max-w-[92%] rounded-lg rounded-tl-none bg-white shadow-sm">
        <p className="whitespace-pre-wrap px-3 pt-2 pb-1 text-[13px] leading-snug text-[#111b21]">{metin}</p>
        <p className="px-3 pb-1.5 text-right text-[10px] text-[#667781]">
          {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
        </p>
        {buton && (
          <div className="flex items-center justify-center gap-1.5 border-t border-[#e9edef] py-2 text-[13px] font-medium text-[#027eb5]">
            <ExternalLink size={13} /> {buton}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WhatsAppGonder({ modul, kayitId, ek, etiket = 'WhatsApp ile gönder', baslik,
  size = 'sm', variant = 'outline', className = '', disabled = false, onGonderildi, testId }) {
  const [acik, setAcik] = useState(false);
  const [veri, setVeri] = useState(null);
  const [alici, setAlici] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [mesgul, setMesgul] = useState(false);

  const ac = async () => {
    setAcik(true);
    setVeri(null);
    try {
      const r = await api.post('/whatsapp/onizle', { modul, kayit_id: kayitId, ek });
      setVeri(r.data);
      setAciklama(r.data.aciklama || '');
      const ilk = r.data.alicilar.find(a => a.gecerli) || r.data.alicilar[0];
      setAlici(ilk?.anahtar || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Önizleme alınamadı');
      setAcik(false);
    }
  };

  const secili = veri?.alicilar.find(a => a.anahtar === alici);
  const sinir = veri?.sinir?.aciklama || 500;
  // Modülün İŞE ÖZEL şablonu Meta'da onaylıysa (veri.ozel) metin sabittir;
  // değilse genel şablon: Konu + düzenlenebilir Açıklama.
  const ozel = veri?.ozel;
  const metin = !veri ? '' : ozel
    ? ozel.metin.replace('{{1}}', temizle(secili?.ad) || 'Değerli Paydaşımız')
    : veri.sablon_metni
      .replace('{{1}}', temizle(secili?.ad) || 'Değerli Paydaşımız')
      .replace('{{2}}', temizle(veri.konu))
      .replace('{{3}}', temizle(aciklama) || '—');

  const gonder = async () => {
    setMesgul(true);
    try {
      const r = await api.post('/whatsapp/gonder', {
        modul, kayit_id: kayitId, ek, alici_anahtar: alici, aciklama });
      const d = r.data?.durum;
      if (d === 'gonderildi') toast.success(`WhatsApp gönderildi: ${secili?.ad}`);
      else if (d === 'deneme') toast.info('Deneme modu: mesaj gönderilmedi, yalnız kaydedildi');
      else if (d === 'engellendi') toast.warning('Numara test listesinde değil — gönderilmedi');
      else toast.error(r.data?.hata || 'Gönderilemedi');
      setAcik(false);
      onGonderildi?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gönderilemedi');
    } finally { setMesgul(false); }
  };

  return (
    <>
      <Button type="button" size={size} variant={variant} disabled={disabled} onClick={ac}
        title={etiket || "WhatsApp ile gönder"} data-testid={testId || `wa-gonder-${modul}`}
        className={`border-[#d0c4b0] text-[#3d6e4e] hover:bg-[#3d6e4e]/10 ${className}`}>
        <MessageCircle size={14} className={etiket ? 'mr-1' : ''} />{etiket}
      </Button>
      <Dialog open={acik} onOpenChange={(v) => !mesgul && setAcik(v)}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto bg-[#faf7f2]" data-testid="wa-gonder-pencere">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle size={18} className="text-[#3d6e4e]" /> WhatsApp ile gönder
            </DialogTitle>
            <DialogDescription>
              {veri ? `${veri.modul_adi} · ${baslik || veri.baslik}` : 'Hazırlanıyor…'}
            </DialogDescription>
          </DialogHeader>
          {veri && (
            <div className="space-y-3">
              {!veri.ayar.hazir && !veri.ayar.dry_run && (
                <p className="text-xs text-[#9b3b2f] bg-[#9b3b2f]/10 border border-[#9b3b2f]/30 rounded-md px-3 py-2 flex gap-2">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  WhatsApp bağlantısı yapılandırılmamış (Admin Paneli → Entegrasyonlar).
                </p>
              )}
              {!ozel && !veri.ayar.dry_run && veri.ayar.sablon_durumu !== 'APPROVED' && veri.ayar.sablon_durumu !== 'bilinmiyor' && (
                <p className="text-xs text-[#9b3b2f] bg-[#9b3b2f]/10 border border-[#9b3b2f]/30 rounded-md px-3 py-2 flex gap-2"
                  data-testid="wa-sablon-uyari">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>
                    <b>"{veri.sablon}"</b> şablonu Meta'da {veri.ayar.sablon_durumu
                      ? <>onaylı değil (durum: {veri.ayar.sablon_durumu})</> : <>bulunamadı</>}. Gönderim Meta'dan
                    geri döner. Şablon adını Admin Paneli → Entegrasyonlar'da kontrol edin.
                  </span>
                </p>
              )}
              {!veri.ayar.dry_run && veri.ayar.test_listesi_var && (
                <p className="text-xs text-[#7a6a2e] bg-[#7a6a2e]/10 border border-[#7a6a2e]/30 rounded-md px-3 py-2 flex gap-2"
                  data-testid="wa-test-listesi-uyari">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>
                    <b>Test modu:</b> mesaj yalnız Admin Paneli → Entegrasyonlar'daki <b>Test numaraları</b> listesine
                    gider. Herkese göndermek için o listeyi boşaltıp kaydedin.
                  </span>
                </p>
              )}
              {veri.ayar.dry_run && (
                <p className="text-xs text-[#7a6a2e] bg-[#7a6a2e]/10 border border-[#7a6a2e]/30 rounded-md px-3 py-2 flex gap-2"
                  data-testid="wa-deneme-uyari">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  Deneme modu açık: mesaj GÖNDERİLMEZ, yalnız kayda düşer.
                </p>
              )}

              <div className="space-y-1">
                <p className="text-xs font-semibold text-[#3F3E39]">Alıcı</p>
                {veri.alicilar.length === 0 && (
                  <p className="text-sm text-[#9b3b2f]">Kayıtta telefon numarası yok.</p>
                )}
                {veri.alicilar.map(a => (
                  <label key={a.anahtar} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm
                    ${a.gecerli ? 'cursor-pointer' : 'opacity-60'}
                    ${alici === a.anahtar ? 'border-[#3d6e4e] bg-[#3d6e4e]/5' : 'border-[#d0c4b0] bg-white'}`}>
                    <input type="radio" name="wa-alici" disabled={!a.gecerli} checked={alici === a.anahtar}
                      onChange={() => setAlici(a.anahtar)} />
                    <span className="flex-1 min-w-0">
                      <span className="font-medium">{a.ad}</span>
                      <span className="text-[#8a7e70]"> · {a.etiket}</span>
                      <span className="block font-mono text-xs text-[#5c554b]">
                        {a.gecerli ? `+${a.numara}` : `${a.telefon} — geçerli cep numarası değil`}
                        {a.test_listesinde === false && !veri.ayar.dry_run && (
                          <span className="ml-1 font-sans font-semibold text-[#9b3b2f]">— test listesinde değil, gönderilemez</span>)}
                      </span>
                    </span>
                  </label>
                ))}
              </div>

              <div className="grid grid-cols-[4.5rem_1fr] gap-x-2 gap-y-1 text-sm">
                <span className="text-xs font-semibold text-[#3F3E39] pt-0.5">Konu</span>
                <span>{veri.konu}</span>
                <span className="text-xs font-semibold text-[#3F3E39] pt-0.5">Bağlantı</span>
                <span className="text-xs text-[#5c554b]">{sureMetni(veri.link_suresi_saat)}</span>
              </div>

              {ozel ? (
                <p className="text-[11px] text-[#3d6e4e] bg-[#3d6e4e]/10 border border-[#3d6e4e]/30 rounded-md px-3 py-2"
                  data-testid="wa-ozel-sablon">
                  Bu bildirim onaylı özel şablonla (<span className="font-mono">{ozel.sablon}</span>) gider; metin
                  sabittir, düzenlenemez.
                </p>
              ) : (
              <div className="space-y-1">
                {veri.ozel_bekleyen && (
                  <p className="text-[11px] text-[#8a7e70]">
                    Özel şablon (<span className="font-mono">{veri.ozel_bekleyen}</span>) Meta onayı bekliyor; şimdilik
                    genel şablonla gider.
                  </p>
                )}
                <div className="flex justify-between">
                  <p className="text-xs font-semibold text-[#3F3E39]">Açıklama</p>
                  <span className={`text-[11px] ${aciklama.length > sinir ? 'text-[#9b3b2f]' : 'text-[#8a7e70]'}`}>
                    {aciklama.length}/{sinir}
                  </span>
                </div>
                <textarea value={aciklama} onChange={(e) => setAciklama(e.target.value)} rows={3}
                  maxLength={sinir} data-testid="wa-aciklama"
                  className="w-full rounded-md border border-[#d0c4b0] bg-white px-3 py-2 text-sm" />
                <p className="text-[11px] text-[#8a7e70]">Satır sonları WhatsApp kuralı gereği tek boşluğa çevrilir.</p>
              </div>
              )}

              <div className="space-y-1">
                <p className="text-xs font-semibold text-[#3F3E39]">WhatsApp'ta görünümü</p>
                <Onizleme metin={metin} buton={ozel ? ozel.buton : veri.buton} />
              </div>

              {veri.son_gonderimler?.length > 0 && (
                <div className="text-xs text-[#5c554b] space-y-1">
                  <p className="font-semibold text-[#3F3E39]">Bu kayda son gönderimler</p>
                  {veri.son_gonderimler.map(m => (
                    <p key={m.id} className="flex flex-wrap items-center gap-1.5">
                      <span>{tarihSaat(m.created_at)}</span>
                      <span>· {m.alici_adi}</span>
                      <DurumRozeti durum={m.durum} />
                      {m.durum === 'okundu' ? <CheckCheck size={12} className="text-[#34b7f1]" />
                        : m.durum === 'iletildi' ? <CheckCheck size={12} /> : m.durum === 'gonderildi' ? <Check size={12} /> : null}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcik(false)} disabled={mesgul} className="border-[#d0c4b0]">Vazgeç</Button>
            <Button onClick={gonder} disabled={!veri || !secili?.gecerli || !temizle(aciklama) || mesgul
              || (!veri.ayar.dry_run && secili?.test_listesinde === false)}
              title={!veri?.ayar?.dry_run && secili?.test_listesinde === false ? 'Bu numara test listesinde değil' : undefined}
              data-testid="wa-gonder-onay" className="bg-[#3d6e4e] text-white hover:bg-[#2d5a3c]">
              <MessageCircle size={14} className="mr-1" /> {mesgul ? 'Gönderiliyor…' : 'Gönder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
